import {
  SendTransactionRequest,
  UserRejectsError,
  useTonAddress,
  useTonConnectUI,
  useTonWallet,
} from '@tonconnect/ui-react';
import { useRef, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { beginCell, toNano } from '@ton/core';

import { log } from '@app/shared/utils/logger';
import {
  useCreateInvoiceMutation,
  useLazyGetInvoiceStatusQuery,
} from '@app/store/slices/payment/paymentApi';
import { useGetCurrentUserQuery } from '@app/store/slices/users/usersApi';
import notification from '@app/shared/utils/notification';
import { BalanceActionType, PriceType } from '@app/shared/types/common';
import { APP_ENV, EXCHANGE_RATES } from '@app/shared/constants/common';
import { useAppDispatch } from '@app/store/hooks';
import { updateBalance } from '@app/store/utils/cacheUpdate';

export const useDepositSection = (currency: PriceType = PriceType.TON) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data: user } = useGetCurrentUserQuery();
  const walletAddress = useTonAddress();
  const wallet = useTonWallet();
  const chainId = wallet?.account.chain ?? '-239';
  const [tonConnectUI] = useTonConnectUI();

  const [amount, setAmount] = useState(currency === PriceType.TON ? '1' : '200');
  const [isWallet, setIsWallet] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const [createInvoice] = useCreateInvoiceMutation();
  const [fetchStatus] = useLazyGetInvoiceStatusQuery();

  useEffect(() => {
    setIsWallet(!!wallet);
    setIsSuccessful(false);
  }, [wallet]);
  const POLLING_INTERVAL_MS = 2_000; // 2 secs

  const tonAmountToPay = useMemo(() => {
    return currency === PriceType.TON
      ? Number(amount)
      : Number(amount) / EXCHANGE_RATES[PriceType.COOKIE];
  }, [amount, currency]);

  const handleSetAmountAndScroll = (amount: string) => {
    setAmount(amount);
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleTopUp = async () => {
    setIsSuccessful(false);

    if (!amount || !user?.id || !walletAddress) {
      return;
    }

    try {
      setIsLocked(true);

      const formattedChainId = APP_ENV === 'dev' ? '-3' : chainId;
      const invoiceData = await createInvoice({
        userId: user.id,
        amount: String(tonAmountToPay),
        chainId: formattedChainId,
        variant: currency,
      }).unwrap();

      if (!(invoiceData.ok && invoiceData.data?.invoiceId && invoiceData.data.destinationAddress)) {
        notification.error(t('errors.invoice'));
        return;
      }

      const memoCell = beginCell()
        .storeUint(0, 32)
        .storeStringTail(invoiceData.data.invoiceId)
        .endCell();

      const payload = {
        validUntil: new Date(invoiceData.data.expiresAt).getTime(),
        messages: [
          {
            address: invoiceData.data.destinationAddress,
            amount: toNano(tonAmountToPay).toString(),
            payload: memoCell.toBoc().toString('base64'),
          },
        ],
      } satisfies SendTransactionRequest;
      await tonConnectUI.sendTransaction(payload, { notifications: [] });

      setIsPolling(true);
      void pollInvoiceStatus(invoiceData.data.invoiceId, invoiceData.data.expiresAt);
    } catch (error: unknown) {
      void log({ level: 'error', message: 'topUp error', error });
      if (error instanceof UserRejectsError) {
        notification.error(t('errors.transaction_user_rejected'));
      } else {
        notification.error(t('errors.transaction'));
      }
      setIsPolling(false);
      setIsLocked(false);
    }
  };

  const pollInvoiceStatus = async (invoiceId: string, expiresAt: string) => {
    try {
      const res = await fetchStatus(invoiceId).unwrap();
      if (!res.ok || !res.data) {
        throw new Error('Transaction status not found');
      }
      const { status } = res.data;

      if (status === 'paid') {
        updateBalance(currency, BalanceActionType.INCREASE, Number(amount), dispatch);
        notification.success(t('common.transaction_success'));
        setIsPolling(false);
        setIsLocked(false);
        setIsSuccessful(true);
      } else if (status === 'expired') {
        notification.error(t('errors.transaction_expired'));
        setIsLocked(false);
        setIsPolling(false);
      } else if (status === 'pending') {
        setTimeout(() => {
          void pollInvoiceStatus(invoiceId, expiresAt);
        }, POLLING_INTERVAL_MS);
      } else {
        notification.info(t(`errors.transaction_${status}`));
        setIsLocked(false);
        setIsPolling(false);
      }
    } catch (err: unknown) {
      void log({ level: 'error', message: 'pollInvoice error', error: err });
      notification.error(t('errors.transaction'));
      setIsLocked(false);
      setIsPolling(false);
    }
  };

  return {
    amount,
    setAmount,
    isPolling,
    handleTopUp,
    scrollRef,
    handleSetAmountAndScroll,
    walletAddress,
    isSuccessful,
    isLocked,
    isWallet,
  };
};
